import {useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {workflowsApiClient} from '../../workflows-api-integration/client.ts';
import {
    onUpdateExecution,
    onUpdateExecutionPhase,
    onUpdateExecutionStep,
} from '../services/subscriptions.ts';
import {
    Execution,
    ExecutionPhase,
    ExecutionStatus,
    ExecutionStep,
    OnUpdateExecutionPhaseSubscription,
    OnUpdateExecutionStepSubscription,
    OnUpdateExecutionSubscription,
} from '../types.ts';
import {executionsKeys} from './useExecutionsQuery.ts';

type Observable<T> = {
    subscribe: (handlers: {
        next: (value: { data: T }) => void;
        error: (err: unknown) => void;
    }) => { unsubscribe: () => void };
};

const TERMINAL_STATUSES: ExecutionStatus[] = [
    ExecutionStatus.SUCCEEDED,
    ExecutionStatus.FAILED,
    ExecutionStatus.CANCELLED,
    ExecutionStatus.TIMED_OUT,
];

const mergePhase = (
    current: Execution,
    incoming: ExecutionPhase,
): Execution => {
    const phases = current.phases ?? [];
    const idx = phases.findIndex((phase) => phase.id === incoming.id);
    const next = idx >= 0
        ? phases.map((phase, i) =>
            i === idx ? {...phase, ...incoming, steps: phase.steps} : phase,
        )
        : [...phases, incoming];
    return {...current, phases: next};
};

const mergeStep = (
    current: Execution,
    incoming: ExecutionStep,
): Execution => {
    const phases = current.phases ?? [];
    const phaseId = incoming.executionPhaseId;
    const phaseIdx = phases.findIndex((phase) => phase.id === phaseId);
    if (phaseIdx < 0) {
        return current;
    }
    const targetPhase = phases[phaseIdx];
    const steps = targetPhase.steps ?? [];
    const stepIdx = steps.findIndex((step) => step.id === incoming.id);
    const nextSteps = stepIdx >= 0
        ? steps.map((step, i) => (i === stepIdx ? {...step, ...incoming} : step))
        : [...steps, incoming];
    const nextPhases = phases.map((phase, i) =>
        i === phaseIdx ? {...phase, steps: nextSteps} : phase,
    );
    return {...current, phases: nextPhases};
};

/**
 * Subscribes to onUpdateExecution / onUpdateExecutionPhase / onUpdateExecutionStep
 * for one execution and merges incoming events into the cached detail record so
 * the EditPage renders progress live. Invalidates the executions list when the
 * execution reaches a terminal state so the list view picks up the final status.
 */
export const useOnExecutionProgress = (executionId?: string) => {

    const queryClient = useQueryClient();

    useEffect(() => {
        if (!executionId) return;

        const detailKey = executionsKeys.detail(executionId);

        const executionObs = workflowsApiClient.graphql({
            query: onUpdateExecution,
            variables: {id: executionId},
        }) as Observable<OnUpdateExecutionSubscription>;

        const phaseObs = workflowsApiClient.graphql({
            query: onUpdateExecutionPhase,
            variables: {executionId},
        }) as Observable<OnUpdateExecutionPhaseSubscription>;

        const stepObs = workflowsApiClient.graphql({
            query: onUpdateExecutionStep,
            variables: {executionId},
        }) as Observable<OnUpdateExecutionStepSubscription>;

        const executionSub = executionObs.subscribe({
            next: ({data}) => {
                const incoming = data?.onUpdateExecution;
                if (!incoming?.id || incoming.id !== executionId) return;
                queryClient.setQueryData<Execution | undefined>(detailKey, (current) => {
                    if (!current) return incoming;
                    return {...current, ...incoming, phases: current.phases};
                });
                if (incoming.executionStatus && TERMINAL_STATUSES.includes(incoming.executionStatus)) {
                    queryClient.invalidateQueries({queryKey: executionsKeys.lists()});
                }
            },
            error: (err) => console.error('onUpdateExecution subscription error', err),
        });

        const phaseSub = phaseObs.subscribe({
            next: ({data}) => {
                const incoming = data?.onUpdateExecutionPhase;
                if (!incoming?.id || incoming.executionId !== executionId) return;
                queryClient.setQueryData<Execution | undefined>(detailKey, (current) => {
                    if (!current) return current;
                    return mergePhase(current, incoming);
                });
            },
            error: (err) => console.error('onUpdateExecutionPhase subscription error', err),
        });

        const stepSub = stepObs.subscribe({
            next: ({data}) => {
                const incoming = data?.onUpdateExecutionStep;
                if (!incoming?.id || incoming.executionId !== executionId) return;
                queryClient.setQueryData<Execution | undefined>(detailKey, (current) => {
                    if (!current) return current;
                    return mergeStep(current, incoming);
                });
            },
            error: (err) => console.error('onUpdateExecutionStep subscription error', err),
        });

        return () => {
            executionSub.unsubscribe();
            phaseSub.unsubscribe();
            stepSub.unsubscribe();
        };
    }, [executionId, queryClient]);
};
